/**
 * Converts $$IDF_Name##uuid$$ and $$PDF_Name##uuid$$ tokens to markdown links
 * so they render as clickable links in both ReactMarkdown and marked.
 *
 * Shared between ChatMessage.tsx (assistant chat responses) and anywhere else
 * that renders backend-generated markdown carrying these entity tags — e.g.
 * MomentumRoutinesPage.tsx's routine run notes.
 */
export function processEntityLinks(text: string): string {
  return text
    .replace(
      /\$\$IDF_([^#$]+)##([^$]+)\$\$/g,
      (_, name, id) => `[${name.trim()}](#INSURED_ID_${id.trim()})`
    )
    .replace(
      /\$\$PDF_([^#$]+)##([^$]+)\$\$/g,
      (_, name, id) => `[${name.trim()}](#POLICY_ID_${id.trim()})`
    );
}

export function stripTemplateMarkers(text: string): string {
  return text
    .replace(/\$\$IDF_[\s\S]*?\$\$/g, '') // Strip any remaining complete IDF markers
    .replace(/\$\$IDF_[\s\S]*$/g, '')       // Strip partial IDF markers during streaming
    .replace(/\$\$PDF_[\s\S]*?\$\$/g, '') // Strip any remaining complete PDF markers
    .replace(/\$\$PDF_[\s\S]*$/g, '')       // Strip partial PDF markers during streaming
    .replace(/\$\$INSUREDCLIENTIDSTART\$\$[\s\S]*?\$\$INSUREDCLIENTIDEND\$\$/g, '') // Strip complete client ID blocks
    .replace(/\$\$INSUREDCLIENTIDSTART\$\$[\s\S]*$/g, '') // Strip partial client ID blocks (streaming, no END yet)
    .replace(/\$\$COIWORKFLOWSTATUSSTART\$\$[\s\S]*?\$\$COIWORKFLOWSTATUSEND\$\$/g, '') // Strip COI workflow status blocks
    // Strip complete renewal/lead status template blocks (start → end, inclusive of all content)
    .replace(/\$\$RENEWAL_MANAGEMENT_STATUS_TEMPLATE_START\$\$[\s\S]*?\$\$RENEWAL_MANAGEMENT_STATUS_TEMPLATE_END\$\$/g, '')
    .replace(/\$\$LEAD_MANAGEMENT_STATUS_TEMPLATE_START\$\$[\s\S]*?\$\$LEAD_MANAGEMENT_STATUS_TEMPLATE_END\$\$/g, '')
    // Strip partial blocks during streaming (start marker present but end not yet arrived)
    .replace(/\$\$RENEWAL_MANAGEMENT_STATUS_TEMPLATE_START\$\$[\s\S]*/g, '')
    .replace(/\$\$LEAD_MANAGEMENT_STATUS_TEMPLATE_START\$\$[\s\S]*/g, '')
    // Strip complete serialized JSON blocks
    .replace(/\$\$RENEWAL_MANAGEMENT_SERIALIZED_STATUS_JSON_START\$\$[\s\S]*?\$\$RENEWAL_MANAGEMENT_SERIALIZED_STATUS_JSON_END\$\$/g, '')
    .replace(/\$\$LEAD_MANAGEMENT_SERIALIZED_STATUS_JSON_START\$\$[\s\S]*?\$\$LEAD_MANAGEMENT_SERIALIZED_STATUS_JSON_END\$\$/g, '')
    // Strip partial JSON blocks during streaming (with or without closing $$ on marker)
    .replace(/\$\$RENEWAL_MANAGEMENT_SERIALIZED_STATUS_JSON_START[\s\S]*/g, '')
    .replace(/\$\$LEAD_MANAGEMENT_SERIALIZED_STATUS_JSON_START[\s\S]*/g, '')
    // Strip can-generate-artifact control blocks from rendered text
    .replace(/\$\$CANGENERATEARTIFACTSTART\$\$[\s\S]*?\$\$CANGENERATEARTIFACTEND\$\$/g, '')
    .replace(/\$\$CANGENERATEARTIFACTSTART\$\$[\s\S]*$/g, '')
    .replace(/\$\$CANGENERATEARTIFACT[\s\S]*$/g, '')
    .replace(/\$\$[A-Z][A-Z0-9_]*(?:_START|_END)\$\$/g, '')
    .replace(/\$\$[A-Z][A-Z0-9_]*(?:_START|_END)\s*$/gm, '')
    .replace(/^\$\$\s*$/gm, '')
    .replace(/\$\$ChartJSDataStart\$\$[\s\S]*?\$\$ChartJSDataEnd\$\$/g, '')
    .replace(/\$\$ChartJSData[A-Za-z]*[\s\S]*$/g, '') // Full or partial chart marker
    .replace(/\$\$IMAGELINK[A-Za-z]*[\s\S]*$/g, '') // Full or partial image marker
    .replace(/\$\$(?:IMAGELIN|IMAGELI|IMAGEL|IMAGE|IMAG|IMA|IM|I)\s*$/g, '') // Building up token by token
    .replace(/\$\$(?:ChartJSDa|ChartJSD|ChartJS|ChartJ|Chart|Char|Cha|Ch|C)\s*$/g, '')
    .replace(/\$\$(?:INSUREDCLIENTIDSTAR|INSUREDCLIENTIDSTA|INSUREDCLIENTIDST|INSUREDCLIENTIDS|INSUREDCLIENTID|INSUREDCLIENTI|INSUREDCLIENT|INSUREDCLIEN|INSUREDCLIE|INSUREDCLI|INSUREDCL|INSUREDC|INSURED|INSURE|INSUR|INSU|INS|IN|I)\s*$/g, '') // Partial client ID marker
    .replace(/\$\$(?:COIWORKFLOWSTATUSTAR|COIWORKFLOWSTATUSTA|COIWORKFLOWSTATUST|COIWORKFLOWSTATUS|COIWORKFLOWSTATU|COIWORKFLOWSTAT|COIWORKFLOWSTA|COIWORKFLOWST|COIWORKFLOWS|COIWORKFLOW|COIWORKFLO|COIWORKFL|COIWORKF|COIWORK|COIWOR|COIWO|COIW|COI|CO|C)\s*$/g, '') // Partial COI workflow marker
    .replace(/\n\n\$\$\s*$/, '') // Trailing $$ before marker starts building
    .trim();
}
